import ObexRequestsHandlerP80 from '../handlers/ObexRequestsHandlerP80';
import { Store, StorePayload } from '../types/Store.types';
import { getAccessToken, getActiveCompanyId, buildQuery, unwrapList, unwrapRecord, unwrapRecordOrObject, unwrapError, getTransportError } from './apiUtils';

const ROUTES = {
  list:   '/stores_list',
  create: '/stores_create',
  update: '/stores_update',
  remove: '/stores_delete'
};

const idOf = (v: any): string => (v !== undefined && v !== null ? String(v) : '');

export const mapStore = (raw: any): Store => ({
  id:              String(raw.id),
  companyId:       idOf(raw.company_id),
  code:            raw.code || '',
  name:            raw.name || '',
  address:         raw.address || '',
  phone:           raw.phone || '',
  email:           raw.email || '',
  warehouseId:     idOf(raw.warehouse_id),
  warehouseName:   raw.warehouse_name || '',
  billingLineId:   idOf(raw.billing_line_id),
  billingLineName: raw.billing_line_name || '',
  billingSeries:   raw.billing_series || '',
  costCenterId:    idOf(raw.cost_center_id),
  costCenterName:  raw.cost_center_code ? `${raw.cost_center_code} · ${raw.cost_center_name || ''}` : '',
  status:          raw.status === 'inactive' ? 'inactive' : 'active',
  posCount:        Number(raw.pos_count) || 0
});

export const listStores = async (options: { companyId?: string; status?: string; search?: string } = {}): Promise<Store[]> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');
  const res = await ObexRequestsHandlerP80.get(`${ROUTES.list}${buildQuery({
    access_token: token,
    company_id:   options.companyId || getActiveCompanyId(),
    status:       options.status,
    search:       options.search
  })}`);
  const transport = getTransportError(res);
  if (transport) throw new Error(transport);
  return unwrapList(res).map(mapStore);
};

const fields = (p: StorePayload) => ({
  name:            p.name,
  address:         p.address,
  phone:           p.phone,
  email:           p.email,
  warehouse_id:    p.warehouseId,
  billing_line_id: p.billingLineId,
  cost_center_id:  p.costCenterId,
  status:          p.status
});

export const createStore = async (payload: StorePayload): Promise<Store> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');
  const res = await ObexRequestsHandlerP80.get(`${ROUTES.create}${buildQuery({
    access_token: token,
    company_id:   payload.companyId || getActiveCompanyId(),
    code:         payload.code,
    ...fields(payload)
  })}`);
  const record = unwrapRecord(res);
  if (!record) throw new Error(unwrapError(res, 'Error al crear la tienda.'));
  return mapStore(record);
};

export const updateStore = async (id: string, payload: StorePayload): Promise<Store> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');
  const res = await ObexRequestsHandlerP80.get(`${ROUTES.update}${buildQuery({ access_token: token, id, ...fields(payload) })}`);
  const record = unwrapRecord(res);
  if (!record) throw new Error(unwrapError(res, 'Error al modificar la tienda.'));
  return mapStore(record);
};

export const deleteStore = async (id: string): Promise<void> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');
  const res = await ObexRequestsHandlerP80.get(`${ROUTES.remove}${buildQuery({ access_token: token, id })}`);
  // El endpoint responde {message}; un error llega como string plano
  const out = unwrapRecordOrObject(res);
  if (!out || !out.message) throw new Error(unwrapError(res, 'Error al eliminar la tienda.'));
};
